import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import type { MapaCargoSecao } from '@/components/Configuracao/mapaCargos/mapaCargosTypes'
import { slugifyMapaCargoSecaoId } from '@/components/Configuracao/mapaCargos/mapaCargosMerge'

type Props = {
  secoes: MapaCargoSecao[]
  onChange: (secoes: MapaCargoSecao[]) => void
}

export function MapaCargosEscopoEditor({ secoes, onChange }: Props) {
  const updateSecao = (index: number, patch: Partial<MapaCargoSecao>) => {
    const next = secoes.map((secao, secaoIndex) => {
      if (secaoIndex !== index) return secao
      const merged = { ...secao, ...patch }
      if (patch.titulo !== undefined) {
        merged.id = slugifyMapaCargoSecaoId(patch.titulo) || secao.id
      }
      return merged
    })
    onChange(next)
  }

  const updateAtividade = (secaoIndex: number, atividadeIndex: number, value: string) => {
    const next = secoes.map((secao, currentSecaoIndex) => {
      if (currentSecaoIndex !== secaoIndex) return secao
      const atividades = secao.atividades.map((item, currentAtividadeIndex) =>
        currentAtividadeIndex === atividadeIndex ? value : item,
      )
      return { ...secao, atividades }
    })
    onChange(next)
  }

  const addAtividade = (secaoIndex: number) => {
    const next = secoes.map((secao, currentSecaoIndex) =>
      currentSecaoIndex === secaoIndex
        ? { ...secao, atividades: [...secao.atividades, ''] }
        : secao,
    )
    onChange(next)
  }

  const removeAtividade = (secaoIndex: number, atividadeIndex: number) => {
    const next = secoes.map((secao, currentSecaoIndex) => {
      if (currentSecaoIndex !== secaoIndex) return secao
      return {
        ...secao,
        atividades: secao.atividades.filter((_, index) => index !== atividadeIndex),
      }
    })
    onChange(next)
  }

  const addSecao = () => {
    onChange([
      ...secoes,
      {
        id: `novo-bloco-${Date.now()}`,
        titulo: '',
        atividades: [''],
      },
    ])
  }

  const removeSecao = (index: number) => {
    onChange(secoes.filter((_, secaoIndex) => secaoIndex !== index))
  }

  return (
    <div className='space-y-4'>
      {secoes.map((secao, secaoIndex) => (
        <article
          key={`${secao.id}-${secaoIndex}`}
          className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'
        >
          <div className='mb-3 flex items-start justify-between gap-3'>
            <div className='flex-1'>
              <Input
                label='Bloco de responsabilidade'
                value={secao.titulo}
                onChange={(event) => updateSecao(secaoIndex, { titulo: event.target.value })}
                placeholder='Ex.: Planejamento e execução de conteúdo estratégico'
              />
            </div>
            <button
              type='button'
              onClick={() => removeSecao(secaoIndex)}
              className='mt-6 rounded-md p-2 text-[var(--text-muted)] transition-colors hover:bg-[rgba(229,72,77,0.08)] hover:text-[#e5484d]'
              aria-label='Remover bloco'
              disabled={secoes.length <= 1}
            >
              <Trash2 size={16} />
            </button>
          </div>

          <div className='space-y-3'>
            {secao.atividades.map((atividade, atividadeIndex) => (
              <div key={`${secao.id}-atividade-${atividadeIndex}`} className='flex gap-2'>
                <Textarea
                  label={atividadeIndex === 0 ? 'Atividades' : undefined}
                  value={atividade}
                  onChange={(event) =>
                    updateAtividade(secaoIndex, atividadeIndex, event.target.value)
                  }
                  rows={2}
                  placeholder='Descreva a atividade'
                  className='min-h-[72px]'
                />
                <button
                  type='button'
                  onClick={() => removeAtividade(secaoIndex, atividadeIndex)}
                  className='mt-6 rounded-md p-2 text-[var(--text-muted)] transition-colors hover:bg-[rgba(229,72,77,0.08)] hover:text-[#e5484d]'
                  aria-label='Remover atividade'
                  disabled={secao.atividades.length <= 1}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <Button
            type='button'
            variant='secondary'
            size='sm'
            className='mt-3'
            icon={<Plus size={14} />}
            onClick={() => addAtividade(secaoIndex)}
          >
            Adicionar atividade
          </Button>
        </article>
      ))}

      <Button type='button' variant='secondary' icon={<Plus size={14} />} onClick={addSecao}>
        Adicionar bloco
      </Button>
    </div>
  )
}
